import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { formatWalletAmount } from '@nebalkon-workspace/cabinet-core';
import type { OrderWalletStatementEntry } from '@nebalkon-workspace/order-api';
import { StaffOrderDetailFacade } from './staff-order-detail.facade';

@Component({
  selector: 'lib-staff-order-detail-wallet-card',
  imports: [TranslatePipe, DatePipe],
  templateUrl: './staff-order-detail-wallet-card.component.html',
  styleUrl: './staff-order-detail-wallet-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffOrderDetailWalletCardComponent {
  readonly facade = inject(StaffOrderDetailFacade);
  readonly #translate = inject(TranslateService);

  protected readonly summary = computed(() => this.facade.walletSummary());
  protected readonly statement = computed(() => this.facade.walletStatement());
  protected readonly statementLoading = computed(() =>
    this.facade.walletStatementLoading(),
  );
  protected readonly canLoadMore = computed(() =>
    this.facade.canLoadMoreWalletStatement(),
  );

  protected readonly formattedBalance = computed(() => {
    const wallet = this.summary();
    if (!wallet) {
      return '';
    }
    return formatWalletAmount(wallet.balance_bynd);
  });

  protected readonly formattedDailyRate = computed(() => {
    const wallet = this.summary();
    if (!wallet || wallet.estimated_daily_charge_bynd <= 0) {
      return '';
    }
    return formatWalletAmount(wallet.estimated_daily_charge_bynd);
  });

  protected readonly isNegative = computed(
    () => this.summary()?.balance_state === 'negative',
  );

  protected readonly statusKey = computed(() => {
    const wallet = this.summary();
    if (!wallet) {
      return 'staff.orders.cockpit.wallet.unavailable';
    }
    if (wallet.balance_state === 'negative') {
      return 'staff.orders.cockpit.wallet.statusNegative';
    }
    if (!wallet.can_create_orders) {
      return 'staff.orders.cockpit.wallet.statusBlocked';
    }
    return 'staff.orders.cockpit.wallet.statusOk';
  });

  protected readonly statusBadgeClass = computed(() => {
    const wallet = this.summary();
    if (!wallet) {
      return 'wallet-card__status--unknown';
    }
    if (wallet.balance_state === 'negative') {
      return 'wallet-card__status--negative';
    }
    if (!wallet.can_create_orders) {
      return 'wallet-card__status--blocked';
    }
    return 'wallet-card__status--ok';
  });

  protected blockReasonLabelKey(reasonCode: string | null | undefined): string {
    switch (reasonCode) {
      case 'wallet_negative_balance':
      case 'insufficient_balance':
        return `staff.orders.cockpit.wallet.blockReason.${reasonCode}`;
      default:
        return 'staff.orders.cockpit.wallet.statusBlocked';
    }
  }

  protected statementTypeLabel(item: OrderWalletStatementEntry): string {
    switch (item.reason_code) {
      case 'storage_daily_charge':
        return 'staff.orders.cockpit.wallet.statement.types.dailyCharge';
      case 'order_service_charge_pickup':
        return 'staff.orders.cockpit.wallet.statement.types.serviceChargePickup';
      case 'order_service_charge_return':
        return 'staff.orders.cockpit.wallet.statement.types.serviceChargeReturn';
      case 'order_service_charge_packaging':
        return 'staff.orders.cockpit.wallet.statement.types.serviceChargePackaging';
      case 'topup_online':
        return 'staff.orders.cockpit.wallet.statement.types.topupOnline';
      case 'topup_manual':
        return 'staff.orders.cockpit.wallet.statement.types.topupManual';
      case 'topup_terminal_cash':
        return 'staff.orders.cockpit.wallet.statement.types.topupTerminalCash';
      case 'topup_terminal_card':
        return 'staff.orders.cockpit.wallet.statement.types.topupTerminalCard';
      case 'topup_payment_link':
        return 'staff.orders.cockpit.wallet.statement.types.topupPaymentLink';
      case 'topup_other':
        return 'staff.orders.cockpit.wallet.statement.types.topupOther';
      case 'refund_correction':
        return 'staff.orders.cockpit.wallet.statement.types.refundCorrection';
      case 'refund_other':
        return 'staff.orders.cockpit.wallet.statement.types.refundOther';
      case 'refund_manual':
        return 'staff.orders.cockpit.wallet.statement.types.refund';
      case 'debit_correction':
        return 'staff.orders.cockpit.wallet.statement.types.debitCorrection';
      case 'debit_storage':
        return 'staff.orders.cockpit.wallet.statement.types.debitStorage';
      case 'debit_transport':
        return 'staff.orders.cockpit.wallet.statement.types.debitTransport';
      case 'debit_rigging':
        return 'staff.orders.cockpit.wallet.statement.types.debitRigging';
      case 'debit_packing_work':
        return 'staff.orders.cockpit.wallet.statement.types.debitPackingWork';
      case 'debit_packing_materials':
        return 'staff.orders.cockpit.wallet.statement.types.debitPackingMaterials';
      case 'debit_other':
        return 'staff.orders.cockpit.wallet.statement.types.debitOther';
      case 'adjustment_manual':
        return 'staff.orders.cockpit.wallet.statement.types.adjustment';
      default:
        return 'staff.orders.cockpit.wallet.statement.types.unknown';
    }
  }

  protected statementContext(item: OrderWalletStatementEntry): {
    key: string;
    params?: Record<string, string>;
  } | null {
    switch (item.reason_code) {
      case 'storage_daily_charge':
        if (item.charge_date) {
          return {
            key: 'staff.orders.cockpit.wallet.statement.context.dailyChargeDate',
            params: { date: item.charge_date },
          };
        }
        return null;
      case 'order_service_charge_pickup':
      case 'order_service_charge_return':
      case 'order_service_charge_packaging':
      case 'order_service_charge':
        if (item.order_short_id) {
          return {
            key: 'staff.orders.cockpit.wallet.statement.context.orderShort',
            params: { orderShortId: item.order_short_id },
          };
        }
        return null;
      case 'topup_online':
        return {
          key: 'staff.orders.cockpit.wallet.statement.context.topupChannel',
          params: { channel: this.topUpChannelLabel(item.topup_channel) },
        };
      default:
        return null;
    }
  }

  protected statementReceiptUrl(item: OrderWalletStatementEntry): string | null {
    const url = item.receipt_url?.trim();
    return url ? url : null;
  }

  protected statementTransactionId(
    item: OrderWalletStatementEntry,
  ): string | null {
    const transactionId = item.transaction_id?.trim();
    return transactionId ? transactionId : null;
  }

  protected statementStaffComment(
    item: OrderWalletStatementEntry,
  ): string | null {
    const comment = item.staff_comment?.trim();
    return comment ? comment : null;
  }

  protected formatStatementAmount(amountBynd: number): string {
    const sign = amountBynd > 0 ? '+' : '';
    return `${sign}${formatWalletAmount(amountBynd)}`;
  }

  private topUpChannelLabel(channel: string | null | undefined): string {
    switch (channel) {
      case 'alfa':
        return 'Alfa Bank';
      default:
        return this.#translate.instant('common.paymentMethod.online');
    }
  }

  protected loadMoreStatement(): void {
    this.facade.loadMoreWalletStatement();
  }
}
